/**
* CheckboxRange add functionality for a number range element.
*
* Template in Stage: https://laakkonen.stage.crasman.fi/admin/manage/scriptlets/scriptleteditor/html/code/show/kpO-w_deQWG-A73HKlRblg
*/
export default class CheckboxRange {

  /**
   * @private
   * @property {HTMLElement} #element - HTMLElement inside checkbox range element that need later access
   */
  #element;

  /**
   * @private
   * @property {object} #dom - HTMLElement inside checkbox range element that need later access
   */
  #dom;

  /**
   * @static
   * @public
   * @property {object} CheckboxRange instances for accessing them from outside.
   *                    Where key is element's id and value is CheckboxRange instance.
   */
  static instances = {};

  /**
   * @constructor
   * @param {HTMlElement} element - element witch class's logic is binded
   * @throws Error when element is missing an id
   */
  constructor(element) {
    this.#element = element;
    this.#validate(element);
    this.#findElements();
    this.#bindEventListeners();

    //Make component accessable from elsewhere.
    CheckboxRange.instances[element.id] = this;
  }

  /**
   * Find highest and lowest selected value
   * @public
   * @return {object} return highest and lowest checked input pairss
   */
  findHighestAndLowest() {
    let min = { value: Number.POSITIVE_INFINITY, inputPair: null },
      max = { value: Number.NEGATIVE_INFINITY, inputPair: null };

    this.#dom.inputs.filter(input => input.checked).forEach(input => {
      const value = parseFloat(input.value);

      if (value < min.value) {
        min.value = value;
        min.inputPair = this.#dom.inputPairs[input.value];
      }

      if (value > max.value) {
        max.value = value;
        max.inputPair = this.#dom.inputPairs[input.value];
      }
    });

    return { min, max };
  }


  /**
   * Init CheckboxRange elements
   * @public
   */
  static init() {
    document.querySelectorAll('.checkbox-range').forEach(element => new CheckboxRange(element));
  }


  /**
   * Check that element is a valid one. If not logs an error mesage to console. Nothing more.
   * @param {HTMLElement} element element to validate
   * @private
   * @throws Error when element is missing an id
   */
  #validate(element) {
    if (!element.id) {
      throw new Error('Checkbox group element is missing an id attribute. This is required to access element later trough JS-logic.')
    }
  }

  /**
   * Populate #dom property with HTMLElement objects for a later use.
   * @private
   */
  #findElements() {
    this.#dom = { inputPairs: {}, inputs: [] }

    const options = this.#element.querySelectorAll('.checkbox-range-option');
    const inputs = [...this.#element.querySelectorAll('input')];

    options.forEach(option => {
      const input = inputs.find(input => input.value === option.dataset.value)
      this.#dom.inputPairs[option.dataset.value] = { option, input, value: input.value };
      this.#dom.inputs.push(input);
    });
  }

  /**
   * Bind event listener functions to HTMLElements
   * @private
   */
  #bindEventListeners() {
    this.#element.addEventListener('click', this.#onCheckboxRangeClicked.bind(this));
  }

  /**
   * Called on checkbox range element is clicked.
   * @private
   * @param {Event} event pointer event object
   */
  #onCheckboxRangeClicked(event) {
    if (event.target.tagName === 'LABEL' && event.target.classList.contains('checkbox-range-option')) {
      this.#onRangeOptionClicked(event);
    }
  }

  /**
   * On reqular range option is clicked.
   * @private
   * @param {Event} event event object
   */
  #onRangeOptionClicked(event) {
    const value = event.target.dataset.value;

    if (value) {
      const checkedInputPairs = Object.keys(this.#dom.inputPairs)
        .filter(key => this.#dom.inputPairs[key].input.checked)

      const allreadyChecked = checkedInputPairs.find(key => this.#dom.inputPairs[key].input.value === value);

      if (this.#element.dataset.mode === 'range' && !allreadyChecked && checkedInputPairs.length === 2) {

        const highestAndLowest = this.findHighestAndLowest();
        const numValue = parseFloat(value);

        const minDistance = Math.abs(highestAndLowest.min.value - numValue)
        const maxDistance = Math.abs(highestAndLowest.max.value - numValue)

        const move = minDistance < maxDistance ? 'min' : 'max';
        this.#toggleInputPair(highestAndLowest[move].inputPair, false);
      }

      this.#toggleInputPair(this.#dom.inputPairs[value], !this.#dom.inputPairs[value].input.checked);

      this.#toggleRangeOptionChecked();
    }
  }

  /**
   * Toggle .range-option-checked class betwheen checked values to highlight selected range.
   */
  #toggleRangeOptionChecked() {
    if (this.#element.dataset.mode === 'range') {
      const checkedInputPairs = Object.keys(this.#dom.inputPairs)
        .filter(key => this.#dom.inputPairs[key].input.checked)

      //range-option-checked from non checked elements
      Object.keys(this.#dom.inputPairs)
        .filter(key => !this.#dom.inputPairs[key].input.checked)
        .forEach(key => this.#dom.inputPairs[key].option.classList.toggle('range-option-checked', false))

      if (checkedInputPairs.length === 2) {
        const highestAndLowest = this.findHighestAndLowest();
        const keys = Object.keys(this.#dom.inputPairs);

        const minIndex = keys.findIndex(key => parseFloat(this.#dom.inputPairs[key].value) === parseFloat(highestAndLowest.min.value));
        const maxIndex = keys.findIndex(key => parseFloat(this.#dom.inputPairs[key].value) === parseFloat(highestAndLowest.max.value));

        if (minIndex > -1 && maxIndex > -1) {
          for (let i = minIndex; i < maxIndex; i++) {
            this.#dom.inputPairs[keys[i]].option.classList.toggle('range-option-checked', true);
          }
        }
      }
    }
  }

  /**
   * Check or uncheck input pair
   * @private
   * @param {object} inputPair - {input: input[type="checkbox"], option: div.checkbox-range-option }
   * @param {boolean} checked - to check value pass true, to uncheck value pass false
   */
  #toggleInputPair(inputPair, checked) {
    inputPair.option.classList.toggle('checked', checked);
  }

}
